import { useMemo, useState } from "react";
import { Icon } from "../components/Icon";
import { seedMerchantIcons } from "../lib/catalogApi";
import type { Institution, MerchantIcon } from "../lib/catalogTypes";
import {
  NSI_JSON_URL,
  PH_CHAINS_REFRESH_NOTE,
  PH_CHAINS_REFRESHED,
  PH_CHAINS_SOURCE,
  nextMerchantSortOrder,
  previewBrandImport,
  type BrandImportResult,
} from "../lib/merchantImport";
import {
  merchantToSeedRow,
  mergeMerchantDrafts,
  type MerchantDraft,
  type MergePair,
} from "../lib/merchantMerge";
import { parseCountryScopeJSON, parseMatchTermsJSON, walletCategoryLabel } from "../lib/catalogTypes";

type Props = {
  rows: MerchantIcon[];
  institutions: Institution[];
  busy: boolean;
  onBusy: (busy: boolean) => void;
  onError: (message: string | null) => void;
  onMessage: (message: string | null) => void;
  onReloaded: () => Promise<void>;
};

function termsLabel(row: MerchantDraft): string {
  return parseMatchTermsJSON(row.matchTermsJSON).join(", ") || "—";
}

function countriesLabel(row: MerchantDraft): string {
  const codes = parseCountryScopeJSON(row.countryScopeJSON);
  if (codes.includes("001")) return codes.length === 1 ? "Global" : `Global · ${codes.filter((c) => c !== "001").join(", ").toUpperCase()}`;
  if (codes.includes("ph")) return "PH";
  return codes.length ? codes.map((c) => c.toUpperCase()).join(", ") : "—";
}

function BrandSide({ row, label }: { row: MerchantDraft; label: string }) {
  return (
    <div className="merge-side">
      <span className="overline">{label}</span>
      <div className="merchant-card-art" style={{ width: 40, height: 40 }}>
        {row.iconURL ? <img src={row.iconURL} alt="" /> : <span className="placeholder">{row.displayName.slice(0, 1)}</span>}
      </div>
      <strong>{row.displayName}</strong>
      <span className="muted">{row.normalizedName}</span>
      <div className="row-actions" style={{ marginTop: "0.35rem", flexWrap: "wrap" }}>
        <span className="badge">{walletCategoryLabel(row.walletCategory)}</span>
        <span className="badge">{row.granularKind}</span>
        <span className="badge">{countriesLabel(row)}</span>
        <span className="badge">{row.sourceKind}</span>
        {row.iconURL ? <span className="badge ok">icon</span> : <span className="badge warn">no art</span>}
      </div>
      <p className="muted" style={{ margin: "0.35rem 0 0", fontSize: "0.78rem" }}>
        Aliases: {termsLabel(row)}
      </p>
      {(row.wikidataId || row.nsiId) && (
        <p className="muted" style={{ margin: "0.15rem 0 0", fontSize: "0.78rem" }}>
          {row.wikidataId ? `Wikidata ${row.wikidataId}` : ""}
          {row.wikidataId && row.nsiId ? " · " : ""}
          {row.nsiId ? `NSI ${row.nsiId}` : ""}
        </p>
      )}
    </div>
  );
}

export function MerchantBrandImportPanel({
  rows,
  institutions,
  busy,
  onBusy,
  onError,
  onMessage,
  onReloaded,
}: Props) {
  const [includeNsi, setIncludeNsi] = useState(true);
  const [includePhChains, setIncludePhChains] = useState(true);
  const [includeInstitutions, setIncludeInstitutions] = useState(true);
  const [progress, setProgress] = useState<string | null>(null);
  const [result, setResult] = useState<BrandImportResult | null>(null);
  const [skippedIds, setSkippedIds] = useState<Set<string>>(() => new Set());

  const exact = useMemo(
    () => (result?.exact ?? []).filter((pair) => !skippedIds.has(pair.id)),
    [result, skippedIds],
  );
  const similar = useMemo(
    () => (result?.similar ?? []).filter((pair) => !skippedIds.has(pair.id)),
    [result, skippedIds],
  );

  async function writeRows(drafts: MerchantDraft[], publish = false) {
    if (drafts.length === 0) return 0;
    const { written } = await seedMerchantIcons(drafts.map(merchantToSeedRow), { publish });
    return written;
  }

  async function runPreview() {
    onBusy(true);
    onError(null);
    onMessage(null);
    setProgress("Starting import preview…");
    try {
      const next = await previewBrandImport(
        rows,
        { includeNsi, includePhChains, includeInstitutions },
        { institutions, onProgress: setProgress },
      );
      setResult(next);
      setSkippedIds(new Set());
      onMessage(
        `Preview: ${next.newDrafts.length} new · ${next.exact.length} exact merge · ${next.similar.length} similar · ${next.nsiSkippedWorld.toLocaleString()} unmapped global NSI rows skipped.`,
      );
    } catch (err) {
      onError(err instanceof Error ? err.message : "Import preview failed");
    } finally {
      setProgress(null);
      onBusy(false);
    }
  }

  async function writeNew() {
    if (!result?.newDrafts.length) return;
    if (!window.confirm(`Write ${result.newDrafts.length} new merchant brand row(s)? Icons stay optional.`)) {
      return;
    }
    onBusy(true);
    onError(null);
    try {
      const start = nextMerchantSortOrder(rows);
      const drafts = result.newDrafts.map((draft, index) => ({
        ...draft,
        recordName: draft.recordName ?? `merchant-${draft.sourceSlug}`,
        sortOrder: draft.sortOrder || start + index * 10,
      }));
      const written = await writeRows(drafts);
      setResult({ ...result, newDrafts: [] });
      onMessage(`Wrote ${written} new brand(s). Publish when you want devices to refresh.`);
      await onReloaded();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Write failed");
    } finally {
      onBusy(false);
    }
  }

  async function applyPair(pair: MergePair, into: "left" | "right") {
    const survivor = into === "left" ? pair.left : pair.right;
    const loser = into === "left" ? pair.right : pair.left;
    const merged = mergeMerchantDrafts(survivor, loser);
    onBusy(true);
    onError(null);
    try {
      await writeRows([merged.survivor, merged.loser]);
      setResult((current) => {
        if (!current) return current;
        return {
          ...current,
          exact: current.exact.filter((row) => row.id !== pair.id),
          similar: current.similar.filter((row) => row.id !== pair.id),
        };
      });
      const warning = merged.warnings.length ? ` ${merged.warnings.join(" ")}` : "";
      onMessage(`Merged ${merged.loser.displayName} into ${merged.survivor.displayName}.${warning}`);
      await onReloaded();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Merge failed");
    } finally {
      onBusy(false);
    }
  }

  async function keepBoth(pair: MergePair) {
    onBusy(true);
    onError(null);
    try {
      const left: MerchantDraft = {
        ...pair.left,
        recordName: pair.left.recordName ?? `merchant-${pair.left.sourceSlug}`,
        isActive: true,
        mergedIntoRecordName: null,
      };
      const right: MerchantDraft = {
        ...pair.right,
        recordName: pair.right.recordName ?? `merchant-${pair.right.sourceSlug}`,
        isActive: true,
        mergedIntoRecordName: null,
      };
      await writeRows([left, right]);
      setResult((current) => {
        if (!current) return current;
        return {
          ...current,
          exact: current.exact.filter((row) => row.id !== pair.id),
          similar: current.similar.filter((row) => row.id !== pair.id),
        };
      });
      onMessage(`Kept both ${pair.left.displayName} and ${pair.right.displayName}.`);
      await onReloaded();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Keep both failed");
    } finally {
      onBusy(false);
    }
  }

  async function bulkExact() {
    if (exact.length === 0) return;
    if (
      !window.confirm(
        `Merge ${exact.length} exact match pair(s) into the left (existing) row? Similar names stay manual.`,
      )
    ) {
      return;
    }
    onBusy(true);
    onError(null);
    try {
      const drafts: MerchantDraft[] = [];
      const warnings: string[] = [];
      for (const pair of exact) {
        const merged = mergeMerchantDrafts(pair.left, pair.right);
        drafts.push(merged.survivor, merged.loser);
        warnings.push(...merged.warnings);
      }
      const written = await writeRows(drafts);
      setResult((current) => (current ? { ...current, exact: [] } : current));
      onMessage(
        `Merged ${exact.length} exact pair(s) (${written} rows written).${
          warnings.length ? ` Warnings: ${warnings.slice(0, 3).join(" ")}` : ""
        }`,
      );
      await onReloaded();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Bulk merge failed");
    } finally {
      onBusy(false);
    }
  }

  async function backfillExisting() {
    if (rows.length === 0) return;
    if (
      !window.confirm(
        `Write Wallet category + granular kind onto ${rows.length} existing merchant row(s)? Netflix and other Simple Icons keep their icons and pick up entertainment/other from the old category hint.`,
      )
    ) {
      return;
    }
    onBusy(true);
    onError(null);
    try {
      const written = await writeRows(rows);
      onMessage(`Backfilled taxonomy on ${written} existing brand(s).`);
      await onReloaded();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Backfill failed");
    } finally {
      onBusy(false);
    }
  }

  function skipPair(id: string) {
    setSkippedIds((prev) => new Set(prev).add(id));
  }

  return (
    <section className="panel" id="import-brands">
      <div className="panel-head">
        <div>
          <span className="overline">Import</span>
          <h2>Import brands</h2>
        </div>
        <div className="row-actions">
          <button className="btn btn-secondary" type="button" disabled={busy || rows.length === 0} onClick={() => void backfillExisting()}>
            Backfill existing categories
          </button>
          <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void runPreview()}>
            <Icon name="upload" size={15} />
            Preview import
          </button>
        </div>
      </div>

      <p className="muted" style={{ marginTop: 0 }}>
        Admin-only. Fetches NSI from jsDelivr and the bundled PH chains seed — never from user
        devices, receipts, or private iCloud. Full NSI is not written to CloudKit; only PH + mapped
        global tags, plus forced names (Grab, GCash, Globe, …).
      </p>

      <div className="row-actions" style={{ flexWrap: "wrap", marginBottom: "0.75rem" }}>
        <label className="checkbox" style={{ margin: 0 }}>
          <input type="checkbox" checked={includeNsi} onChange={(e) => setIncludeNsi(e.target.checked)} />
          NSI (PH + mapped 001)
        </label>
        <label className="checkbox" style={{ margin: 0 }}>
          <input
            type="checkbox"
            checked={includePhChains}
            onChange={(e) => setIncludePhChains(e.target.checked)}
          />
          PH chains seed
        </label>
        <label className="checkbox" style={{ margin: 0 }}>
          <input
            type="checkbox"
            checked={includeInstitutions}
            onChange={(e) => setIncludeInstitutions(e.target.checked)}
          />
          Institution names (merge brands, do not copy Institution records)
        </label>
      </div>

      <p className="muted" style={{ fontSize: "0.8rem", marginTop: 0 }}>
        NSI: <code>{NSI_JSON_URL}</code>
        <br />
        PH seed from {PH_CHAINS_SOURCE}
        {PH_CHAINS_REFRESHED ? ` (refreshed ${PH_CHAINS_REFRESHED})` : ""}. {PH_CHAINS_REFRESH_NOTE}
      </p>

      {progress && <div className="banner info">{progress}</div>}

      {result && (
        <>
          <dl className="kv" style={{ marginBottom: "1rem" }}>
            <div>
              <dt>Incoming after filter</dt>
              <dd>{result.incoming.toLocaleString()}</dd>
            </div>
            <div>
              <dt>New</dt>
              <dd>{result.newDrafts.length.toLocaleString()}</dd>
            </div>
            <div>
              <dt>Exact merge</dt>
              <dd>{exact.length.toLocaleString()}</dd>
            </div>
            <div>
              <dt>Similar (manual)</dt>
              <dd>{similar.length.toLocaleString()}</dd>
            </div>
            <div>
              <dt>Skipped in queue</dt>
              <dd>{skippedIds.size.toLocaleString()}</dd>
            </div>
          </dl>

          <div className="row-actions" style={{ marginBottom: "1rem" }}>
            <button
              className="btn btn-brass"
              type="button"
              disabled={busy || result.newDrafts.length === 0}
              onClick={() => void writeNew()}
            >
              Write {result.newDrafts.length} new
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              disabled={busy || exact.length === 0}
              onClick={() => void bulkExact()}
            >
              Merge all exact matches
            </button>
          </div>

          {result.newDrafts.length > 0 && (
            <p className="muted" style={{ fontSize: "0.83rem" }}>
              First new brands:{" "}
              {result.newDrafts
                .slice(0, 12)
                .map((row) => row.displayName)
                .join(" · ")}
              {result.newDrafts.length > 12 ? ` · +${result.newDrafts.length - 12} more` : ""}
            </p>
          )}

          {exact.length > 0 && (
            <div className="merge-queue">
              <h3>Exact matches</h3>
              {exact.slice(0, 40).map((pair) => (
                <MergeCard
                  key={pair.id}
                  pair={pair}
                  busy={busy}
                  onMergeLeft={() => void applyPair(pair, "left")}
                  onMergeRight={() => void applyPair(pair, "right")}
                  onKeepBoth={() => void keepBoth(pair)}
                  onSkip={() => skipPair(pair.id)}
                />
              ))}
              {exact.length > 40 && (
                <p className="muted">Showing 40 of {exact.length}. Use “Merge all exact matches” for the rest.</p>
              )}
            </div>
          )}

          {similar.length > 0 && (
            <div className="merge-queue">
              <h3>Similar names — review manually</h3>
              {similar.map((pair) => (
                <MergeCard
                  key={pair.id}
                  pair={pair}
                  busy={busy}
                  onMergeLeft={() => void applyPair(pair, "left")}
                  onMergeRight={() => void applyPair(pair, "right")}
                  onKeepBoth={() => void keepBoth(pair)}
                  onSkip={() => skipPair(pair.id)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function MergeCard({
  pair,
  busy,
  onMergeLeft,
  onMergeRight,
  onKeepBoth,
  onSkip,
}: {
  pair: MergePair;
  busy: boolean;
  onMergeLeft: () => void;
  onMergeRight: () => void;
  onKeepBoth: () => void;
  onSkip: () => void;
}) {
  return (
    <article className="merge-pair">
      <BrandSide row={pair.left} label={pair.left.recordName ? "Existing" : "Incoming A"} />
      <BrandSide row={pair.right} label={pair.right.recordName ? "Existing" : "Incoming B"} />
      <div className="merge-actions">
        <span className={`badge ${pair.exact ? "ok" : "warn"}`}>{pair.reason}</span>
        <button className="btn btn-primary btn-sm" type="button" disabled={busy} onClick={onMergeLeft}>
          Merge into left
        </button>
        <button className="btn btn-secondary btn-sm" type="button" disabled={busy} onClick={onMergeRight}>
          Merge into right
        </button>
        <button className="btn btn-secondary btn-sm" type="button" disabled={busy} onClick={onKeepBoth}>
          Keep both
        </button>
        <button className="btn btn-quiet btn-sm" type="button" disabled={busy} onClick={onSkip}>
          Skip
        </button>
      </div>
    </article>
  );
}
